import React, { useState, useRef } from 'react';
import { MapPin, Home, Bed, Bath, Square, Heart, Share2, Phone, Mail, Filter, List, Grid, Loader, AlertCircle } from 'lucide-react';
import { useMapProperties } from '../../hooks/useMapProperties';

const MapView = () => {
    const { properties, loading, error } = useMapProperties({ limit: 50 });
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [mapCenter, setMapCenter] = useState([-1.2921, 36.8219]); // Nairobi coordinates
    const [zoom, setZoom] = useState(12);
    const [showPropertyList, setShowPropertyList] = useState(false);
    const [filters, setFilters] = useState({
        priceRange: [0, 200000],
        propertyType: 'all',
        bedrooms: 'all',
        bathrooms: 'all'
    });
    const mapRef = useRef(null);

    const filteredProperties = properties.filter(property => {
        const matchesPrice = property.price >= filters.priceRange[0] && property.price <= filters.priceRange[1];
        const matchesType = filters.propertyType === 'all' || (property.type || '').toLowerCase() === filters.propertyType;
        const matchesBedrooms = filters.bedrooms === 'all' || property.bedrooms >= parseInt(filters.bedrooms);
        const matchesBathrooms = filters.bathrooms === 'all' || property.bathrooms >= parseInt(filters.bathrooms);

        return matchesPrice && matchesType && matchesBedrooms && matchesBathrooms;
    });

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0,
        }).format(price);
    };

    const MapMarker = ({ property, isSelected, onClick }) => (
        <div
            className={`absolute z-10 cursor-pointer transform -translate-x-1/2 -translate-y-full ${
                isSelected ? 'z-20' : ''
            }`}
            style={{
                left: `${((property.coordinates[1] - mapCenter[1]) * 1000 + 50)}%`,
                top: `${((property.coordinates[0] - mapCenter[0]) * -1000 + 50)}%`
            }}
            onClick={() => onClick(property)}
        >
            <div
                className={`relative bg-white rounded-lg shadow-lg border-2 transition-all duration-200 ${
                    isSelected ? 'border-highland-500 scale-110' : 'border-soil-100 hover:border-highland-400'
                }`}
            >
                <div className="px-3 py-2">
                    <div className="font-semibold text-sm text-soil-900">
                        {formatPrice(property.price)}
                    </div>
                    <div className="text-xs text-soil-600">
                        {property.bedrooms}BR • {property.bathrooms}BA
                    </div>
                </div>
                <div
                    className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-8 border-transparent ${
                        isSelected ? 'border-t-highland-500' : 'border-t-soil-100'
                    }`}
                />
            </div>
        </div>
    );

    const PropertyCard = ({ property, isSelected }) => (
        <div
            className={`bg-white rounded-lg shadow-md border transition-all duration-200 cursor-pointer ${
                isSelected ? 'border-highland-500 shadow-lg' : 'border-soil-100 hover:border-highland-400'
            }`}
            onClick={() => setSelectedProperty(property)}
        >
            <div className="relative">
                <img
                    src={property.image}
                    alt={property.title}
                    className="w-full h-48 object-cover rounded-t-lg"
                />
                <button className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-md hover:bg-soil-50">
                    <Heart className="w-4 h-4 text-soil-600" />
                </button>
                <div className="absolute bottom-3 left-3 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-sm">
                    {property.type}
                </div>
            </div>

            <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-lg text-soil-900 leading-tight">
                        {property.title}
                    </h3>
                    <button className="p-1 hover:bg-soil-100 rounded">
                        <Share2 className="w-4 h-4 text-soil-600" />
                    </button>
                </div>

                <div className="font-mono text-2xl font-bold text-highland-500 mb-2">
                    {formatPrice(property.price)}
                    <span className="text-sm font-normal text-soil-600">/month</span>
                </div>

                <div className="flex items-center text-soil-600 mb-3 space-x-4">
                    <div className="flex items-center">
                        <Bed className="w-4 h-4 mr-1" />
                        <span className="text-sm">{property.bedrooms} bed</span>
                    </div>
                    <div className="flex items-center">
                        <Bath className="w-4 h-4 mr-1" />
                        <span className="text-sm">{property.bathrooms} bath</span>
                    </div>
                    <div className="flex items-center">
                        <Square className="w-4 h-4 mr-1" />
                        <span className="text-sm">{property.area} m²</span>
                    </div>
                </div>

                <div className="flex items-center text-soil-600 mb-3">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span className="text-sm">{property.location}</span>
                </div>

                <div className="flex flex-wrap gap-1 mb-3">
                    {property.features.slice(0, 3).map((feature, index) => (
                        <span
                            key={index}
                            className="px-2 py-1 bg-soil-100 text-soil-600 rounded-full text-xs"
                        >
              {feature}
            </span>
                    ))}
                    {property.features.length > 3 && (
                        <span className="px-2 py-1 bg-soil-100 text-soil-600 rounded-full text-xs">
              +{property.features.length - 3} more
            </span>
                    )}
                </div>

                <div className="border-t pt-3">
                    <div className="flex justify-between items-center">
                        <div>
                            <div className="font-medium text-sm text-soil-900">
                                {property.agent.name}
                            </div>
                            <div className="text-xs text-soil-600">
                                {property.agent.company}
                            </div>
                        </div>
                        <div className="flex space-x-2">
                            <button className="p-2 bg-highland-50 text-highland-500 rounded-full hover:bg-highland-100">
                                <Phone className="w-4 h-4" />
                            </button>
                            <button className="p-2 bg-jacaranda-100 text-jacaranda-600 rounded-full hover:bg-jacaranda-100/70">
                                <Mail className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-karibu-100 pt-20">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 py-4">
                        <div>
                            <h1 className="font-display text-2xl font-bold text-soil-900">Property Map</h1>
                            <p className="text-soil-600">{filteredProperties.length} properties found</p>
                        </div>

                        <div className="flex items-center gap-3 flex-wrap">
                            {/* Filter Button */}
                            <button className="flex items-center px-4 py-2 bg-white border border-soil-100 rounded-lg hover:bg-soil-50">
                                <Filter className="w-4 h-4 mr-2" />
                                Filters
                            </button>

                            {/* View Toggle */}
                            <div className="flex items-center bg-soil-100 rounded-lg p-1">
                                <button
                                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                                        !showPropertyList
                                            ? 'bg-white text-soil-900 shadow-sm'
                                            : 'text-soil-600 hover:text-soil-900'
                                    }`}
                                    onClick={() => setShowPropertyList(false)}
                                >
                                    <Home className="w-4 h-4 mr-2" />
                                    Map
                                </button>
                                <button
                                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                                        showPropertyList
                                            ? 'bg-white text-soil-900 shadow-sm'
                                            : 'text-soil-600 hover:text-soil-900'
                                    }`}
                                    onClick={() => setShowPropertyList(true)}
                                >
                                    <List className="w-4 h-4 mr-2" />
                                    List
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader className="w-10 h-10 text-highland-500 animate-spin mr-3" />
                    <span className="text-lg text-soil-600">Loading properties...</span>
                </div>
            ) : error ? (
                <div className="flex items-center justify-center py-20">
                    <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
                        <AlertCircle className="h-12 w-12 text-murram-500 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-soil-900 mb-2">Failed to Load Properties</h2>
                        <p className="text-soil-600">{error}</p>
                    </div>
                </div>
            ) : (
            <div className="flex h-[calc(100vh-5rem)]">
                {/* Map Section */}
                {!showPropertyList && (
                    <div className="flex-1 relative">
                        <div
                            ref={mapRef}
                            className="w-full h-full bg-gradient-to-br from-highland-50 to-karibu-200 relative overflow-hidden"
                            style={{
                                backgroundImage: `
                  radial-gradient(circle at 20% 50%, rgba(31, 77, 61, 0.12), transparent 50%),
                  radial-gradient(circle at 80% 20%, rgba(226, 162, 59, 0.15), transparent 50%),
                  radial-gradient(circle at 40% 80%, rgba(124, 111, 166, 0.12), transparent 50%)
                `
                            }}
                        >
                            {/* Map Markers */}
                            {filteredProperties.filter((property) => property.coordinates).map((property) => (
                                <MapMarker
                                    key={property.id}
                                    property={property}
                                    isSelected={selectedProperty?.id === property.id}
                                    onClick={setSelectedProperty}
                                />
                            ))}

                            {/* Map Controls */}
                            <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg p-2">
                                <button className="block w-8 h-8 mb-1 bg-soil-50 hover:bg-soil-100 rounded text-soil-600 text-xl font-bold">
                                    +
                                </button>
                                <button className="block w-8 h-8 bg-soil-50 hover:bg-soil-100 rounded text-soil-600 text-xl font-bold">
                                    −
                                </button>
                            </div>

                            {/* Legend */}
                            <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3">
                                <h4 className="font-semibold text-sm mb-2">Property Types</h4>
                                <div className="space-y-1 text-xs">
                                    <div className="flex items-center">
                                        <div className="w-3 h-3 bg-highland-500 rounded mr-2"></div>
                                        <span>Apartments</span>
                                    </div>
                                    <div className="flex items-center">
                                        <div className="w-3 h-3 bg-dawn-500 rounded mr-2"></div>
                                        <span>Houses</span>
                                    </div>
                                    <div className="flex items-center">
                                        <div className="w-3 h-3 bg-jacaranda-500 rounded mr-2"></div>
                                        <span>Villas</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Property Details Sidebar / Full List */}
                <div className={`${showPropertyList ? 'w-full' : 'w-96'} bg-white border-l overflow-y-auto`}>
                    <div className="p-4">
                        {showPropertyList ? (
                            // Full Property List View
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredProperties.map((property) => (
                                    <PropertyCard
                                        key={property.id}
                                        property={property}
                                        isSelected={selectedProperty?.id === property.id}
                                    />
                                ))}
                            </div>
                        ) : (
                            // Map Sidebar View
                            <>
                                {selectedProperty ? (
                                    <PropertyCard
                                        property={selectedProperty}
                                        isSelected={true}
                                    />
                                ) : (
                                    <div className="text-center py-8">
                                        <MapPin className="w-12 h-12 text-soil-400 mx-auto mb-4" />
                                        <h3 className="text-lg font-medium text-soil-900 mb-2">
                                            Select a Property
                                        </h3>
                                        <p className="text-soil-600">
                                            Click on any marker on the map to view property details
                                        </p>
                                    </div>
                                )}

                                {/* Quick Property List */}
                                <div className="mt-6">
                                    <h3 className="font-semibold text-soil-900 mb-3">All Properties</h3>
                                    <div className="space-y-2">
                                        {filteredProperties.map((property) => (
                                            <div
                                                key={property.id}
                                                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                                                    selectedProperty?.id === property.id
                                                        ? 'bg-highland-50 border border-highland-100'
                                                        : 'bg-soil-50 hover:bg-soil-100'
                                                }`}
                                                onClick={() => setSelectedProperty(property)}
                                            >
                                                <div className="font-medium text-sm text-soil-900 mb-1">
                                                    {property.title}
                                                </div>
                                                <div className="font-mono text-highland-500 font-semibold">
                                                    {formatPrice(property.price)}
                                                </div>
                                                <div className="text-xs text-soil-600">
                                                    {property.bedrooms}BR • {property.bathrooms}BA • {property.location}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            )}
        </div>
    );
};

export default MapView;